"use client"

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthLayout from '@/components/auth/AuthLayout'
import { Button } from '@/components/ui/Button'

const ResetPasswordCode = () => {
  const [codes, setCodes] = useState(['', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [timeLeft, setTimeLeft] = useState(120) // 2 minutes countdown
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [timeLeft])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d?$/.test(value)) return

    const newCodes = [...codes]
    newCodes[index] = value
    setCodes(newCodes)

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const code = codes.join('')
    if (code.length !== 4) {
      setError('Please enter the complete 4-digit code')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      // Store userId and otp in sessionStorage for NewPassword component
      const userId = sessionStorage.getItem('userId')
      if (!userId) {
        setError('Session expired. Please try again.')
        navigate('/forgot-password', { replace: true })
        return
      }
      
      sessionStorage.setItem('otp', code)
      navigate('/new-password')
    } catch {
      setError('Invalid verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    try {
      // TODO: Implement resend code logic
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API call
      setTimeLeft(120) // Reset countdown
      setCodes(['', '', '', ''])
    } catch {
      setError('Failed to resend code. Please try again.')
    }
  }

  return (
    <AuthLayout>
      {/* Form Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-satoshi">Reset Password</h2>
        <p className="text-gray-600 font-satoshi">A verification Code has been sent to example@email.com.</p>
      </div>

      {/* Verification Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-satoshi">
            {error}
          </div>
        )}

        {/* Code Input Fields */}
        <div className="flex justify-center space-x-3">
          {codes.map((code, index) => (
            <input
              key={index}
              ref={el => { inputRefs.current[index] = el }}
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              maxLength={1}
              className="w-16 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi"
            />
          ))}
        </div>

        {/* Proceed Button */}
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={codes.join('').length !== 4}
          className="w-full font-medium py-3 px-4 rounded-lg font-satoshi"
        >
          {isLoading ? 'Proceeding...' : 'Proceed'}
        </Button>

        {/* Resend Code */}
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600 font-satoshi">
            Didn't receive code?{' '}
            {timeLeft > 0 ? (
              <span className="text-gray-500">Resend code ({formatTime(timeLeft)})</span>
            ) : (
              <button
                type="button"
                onClick={handleResendCode}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Resend code
              </button>
            )}
          </p>
        </div>
      </form>
    </AuthLayout>
  )
}

export default ResetPasswordCode
